import { collection, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebaseConfig.js';

/**
 * Generates a random referral code
 * @param {number} length - Length of the referral code (default: 8)
 * @returns {string} Random referral code
 */
function generateRandomCode(length = 8) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  
  return result;
}

/**
 * Checks if a referral code already exists in the referral_codes collection
 * @param {string} code - The referral code to check
 * @returns {Promise<boolean>} True if code exists, false otherwise
 */
export async function checkReferralCodeExists(code) {
  try {
    const q = query(
      collection(db, 'referral_codes'),
      where('code', '==', code)
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking referral code existence:', error);
    throw error;
  }
}

/**
 * Generates a unique referral code that doesn't exist in the database
 * @param {number} maxAttempts - Maximum attempts to generate unique code (default: 10)
 * @returns {Promise<string>} Unique referral code
 */
export async function generateUniqueReferralCode(maxAttempts = 10) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const code = generateRandomCode();
    
    try {
      const exists = await checkReferralCodeExists(code);
      if (!exists) {
        return code;
      }
    } catch (error) {
      console.error(`Attempt ${attempt + 1} failed:`, error);
      if (attempt === maxAttempts - 1) {
        throw new Error('Failed to generate unique referral code after maximum attempts');
      }
    }
  }
  
  throw new Error('Failed to generate unique referral code');
}

/**
 * Validates referral code format
 * @param {string} code - The referral code to validate
 * @returns {boolean} True if valid format, false otherwise
 */
export function validateReferralCodeFormat(code) {
  if (!code || typeof code !== 'string') {
    return false;
  }
  
  // Check if code is 8 characters long and contains only alphanumeric characters
  const regex = /^[A-Z0-9]{8}$/;
  return regex.test(code);
}

/**
 * Validates if a referral code exists and is active
 * @param {string} code - The referral code to validate
 * @returns {Promise<{valid: boolean, userId?: string}>} Validation result with user ID if valid
 */
export async function validateReferralCode(code) {
  try {
    // First check format
    if (!validateReferralCodeFormat(code)) {
      return { valid: false };
    }
    
    // Get current user to prevent self-referral
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    // Build query - exclude current user's codes if authenticated
    let q;
    if (currentUser) {
      q = query(
        collection(db, 'referral_codes'),
        where('code', '==', code),
        where('userId', '!=', currentUser.uid)
      );
    } else {
      q = query(
        collection(db, 'referral_codes'),
        where('code', '==', code)
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { valid: false };
    }
    
    // Get the first (and should be only) document
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    return {
      valid: true,
      userId: data.userId
    };
  } catch (error) {
    console.error('Error validating referral code:', error);
    return { valid: false };
  }
}